import { useEffect, useState } from "react";
import { Loader2, Inbox } from "lucide-react";
import { getMyRedemptions } from "@/lib/api";

const STATUS_COPY = {
  submitted: { label: "Submitted", tone: "info" },
  pending_partner_review: { label: "Pending review", tone: "info" },
  fulfilled: { label: "Fulfilled", tone: "ok" },
  rejected: { label: "Rejected", tone: "bad" },
  failed_retryable: { label: "Temporarily failed", tone: "warn" },
  failed_terminal: { label: "Failed", tone: "bad" },
};

const TONE_CLASS = {
  info: "text-[#00F0FF] border-[#00F0FF]/40",
  ok: "text-[#39FF14] border-[#39FF14]/40",
  warn: "text-[#FFB800] border-[#FFB800]/40",
  bad: "text-[#FF3B30] border-[#FF3B30]/40",
};

function formatTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return "—";
  }
}

function shortRef(ref) {
  if (!ref) return "";
  return ref.length > 18 ? `${ref.slice(0, 12)}…${ref.slice(-4)}` : ref;
}

/**
 * Recent redemptions list. Authenticated-only.
 *
 * `linked` (bool) lifts the session decision to the parent — the
 * unauthenticated branch must NOT show "error" copy; it shows a
 * friendly empty state per the AP-4 plan.
 */
export default function RecentRedemptions({ linked, refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(linked);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!linked) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const out = await getMyRedemptions(20);
        if (alive) setData(out);
      } catch (e) {
        if (alive) setError("network");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [linked, refreshKey]);

  if (!linked) {
    return (
      <div data-testid="redeem-recent-unlinked" className="glass rounded-md p-6">
        <div className="flex items-start gap-3">
          <Inbox className="mt-1 h-4 w-4 text-white/40" />
          <div>
            <h3 className="font-display text-sm uppercase tracking-[0.2em] text-white">
              Your redemptions
            </h3>
            <p className="mt-1 font-mono text-[11px] text-white/60">
              Link your Assos365 account to see your redemption history here.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="redeem-recent-list" className="glass rounded-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-sm uppercase tracking-[0.2em] text-white">
          Your redemptions
        </h3>
        <span className="font-mono text-[10px] text-white/40">
          {data ? `${data.count} recent` : ""}
        </span>
      </div>

      {loading && (
        <p className="flex items-center gap-2 font-mono text-xs text-white/50">
          <Loader2 className="h-4 w-4 animate-spin" /> loading…
        </p>
      )}

      {error && (
        <p data-testid="redeem-recent-error" className="font-mono text-xs text-[#FF3B30]">
          Could not load your redemption history. Try again later.
        </p>
      )}

      {!loading && !error && data?.count === 0 && (
        <p data-testid="redeem-recent-empty" className="font-mono text-[11px] text-white/50">
          No redemptions yet. Submit your first one once AP-4F ships.
        </p>
      )}

      {!loading && !error && data?.count > 0 && (
        <ul className="divide-y divide-white/5" data-testid="redeem-recent-rows">
          {data.items.map((row) => {
            const meta = STATUS_COPY[row.status] || { label: row.status, tone: "info" };
            return (
              <li
                key={row.arc_reference_id}
                data-testid={`redeem-recent-row-${row.arc_reference_id}`}
                className="py-3 flex items-center gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-xs text-white/85 truncate">
                    {shortRef(row.arc_reference_id)}
                  </div>
                  <div className="font-mono text-[10px] text-white/40">
                    {formatTs(row.created_at)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-sm text-[#39FF14]">
                    {row.rate_snapshot?.eur_amount ?? "—"}
                    <span className="ml-1 text-[10px] text-[#00F0FF]">EUR</span>
                  </div>
                  <div className="font-mono text-[10px] text-white/40">
                    {row.rate_snapshot?.arc_amount ?? "—"} ARC
                  </div>
                </div>
                <span
                  className={`shrink-0 rounded border px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${TONE_CLASS[meta.tone] || TONE_CLASS.info}`}
                >
                  {meta.label}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
